import { Evaluator, parseFormula, valueToString } from "formula-engine";
import Vue from "vue";
import { FormulaContext, VariableLookupFunction } from "~/formulaContext";
import { ObjectHelper } from '~/helpers/objectHelper';
import { IDynamicDataConfig, IDynamicIdComponents } from "~/interfaces/dynamicFieldsInterfaces";
import { TPartialBy } from "~/interfaces/generalInterfaces";
import CodeMirrorAble from "../helpers/codeMirrorable";
import TrackedModel from "./tracked/trackedModel";

export default class Formula extends CodeMirrorAble(TrackedModel) {
  // @note do not use ":", "|", "-" in meta prefix as they are reserved by formula parser
  static readonly DYNAMIC_PREFIX: string = 'f'
  static readonly ID_FORMAT: string = '[a-z0-9]{1,32}'
  static readonly DYNAMIC_META_PREFIX: string = 'meta'
  // static readonly DYNAMIC_REFERENCE_TYPE_PREFIX: string = 'drt' // I preferred to duplicate 'drt' everywhere because it's easier to read
  static readonly DYNAMIC_ID_REGEXP: RegExp = new RegExp(`${Formula.DYNAMIC_PREFIX}_${Formula.ID_FORMAT}(_drt\\d+)?(_${Formula.DYNAMIC_META_PREFIX}[^\\s])?`, 'g'); // optional metadata at end
  fieldName: string;
  text: string;
  originalText: string;
  evaluator: Evaluator;
  private result: string | null;

  constructor(parent: object, fieldName: string, fieldLookupFunction: VariableLookupFunction = null) {
    super();
    this.fieldName = fieldName;
    this.parent = parent;
    this.text = this.parent[`${this.fieldName}_formula`];
    this.originalText = this.text;
    this.evaluator = new Evaluator(new FormulaContext(fieldLookupFunction));
  }

  /**
   *
   * @param dynamicId {String} should have the following shape
   *   <prefix>_<id>_<optional DynamicReferenceType>_<optional_metadata>
   * @example
   *   Formula.parseDynamicId("f_abc-123_drt1_meta1") => {prefix: "f", id: "abc-123", dynamicReferenceType: DynamicReferenceType.SubmissionId, meta: "1"}
   */
  static parseDynamicId(dynamicId: string): IDynamicIdComponents {
    let [prefix, id, ...rest] = dynamicId.split('_')
    let dynamicReferenceType = null;
    let meta = null;

    rest.forEach(function(substring) {
      if (substring.startsWith('drt')) {
        const dynamicReferenceTypeString = substring.split('drt')[1];
        dynamicReferenceType = parseInt(dynamicReferenceTypeString, 10);
      } else if (substring.startsWith(Formula.DYNAMIC_META_PREFIX)) {
        meta = substring.split(Formula.DYNAMIC_META_PREFIX)[1];
      }
    });

    return {prefix, id, dynamicReferenceType, meta}
  }

  /**
   * Turns id components into single string
   * @example
   *   Formula.buildId({prefix: "f", id: "abc-123", dynamicReferenceType: DynamicReferenceType.SubmissionId, meta: "1"} => "f_abc-123_drt1_meta1")
   * @param idComponents the pieces of the id to put together
   */
  static buildDynamicId(idComponents: TPartialBy<IDynamicIdComponents, 'prefix' | 'meta'>): string {
    let id: string = idComponents.id
    if (idComponents.prefix) id = `${idComponents.prefix}_` + id
    if (idComponents.dynamicReferenceType) id += `_drt${idComponents.dynamicReferenceType}`;
    if (idComponents.meta) id += `_${Formula.DYNAMIC_META_PREFIX}${idComponents.meta}`
    return id
  }

  static clientIdToFormulaId(clientId: string): string {
    return clientId.replace(/-/g, '')
  }

  static formulaIdToClientId(formulaId: string): string {
    const {id: flatClientId} = Formula.parseDynamicId(formulaId)
    return Formula.expandFlatClientId(flatClientId)
  }

  static expandFlatClientId(flatClientId: string): string {
    return flatClientId.replace(/(\w{8})(\w{4})(\w{4})(\w{4})(\w{12})/, '$1-$2-$3-$4-$5')
  }

  /**
   * Remove properties that likely should now be inside an HTML string for a formula pill
   * @example 'path'
   *   path is used as an array of ReferencePathNode as should not be stored
   *   as a jsonified string in the HTML
   *
   * @param data {IDynamicDataConfig}
   */
  static sliceDynamicDataset(data: IDynamicDataConfig) {
    const allowedKeys: (keyof IDynamicDataConfig)[] = Object.keys(data) as (keyof IDynamicDataConfig)[]
    allowedKeys.splice(allowedKeys.indexOf('path'), 1)
    return ObjectHelper.sliceProperties(data,  ...allowedKeys)
  }

  static parseVariables(formula: string): string[] {
    return formula.match(Formula.DYNAMIC_ID_REGEXP) || []
  }

  static parseClientIds(formula: string): string[] {
    return Formula.parseVariables(formula)
      .map(variable => Formula.formulaIdToClientId(variable))
  }

  static replaceVariables(formula: string, callback: (id: string) => string): string {
    return formula?.replace(Formula.DYNAMIC_ID_REGEXP, formulaId => {
      return callback(Formula.formulaIdToClientId(formulaId))
    })
  }

  updateParent() {
    if (this.parent) {
      Vue.set(this.parent, `${this.fieldName}_formula`, this.text.trim());
    }
  }

  isComplete(): boolean {
    try {
      return this.text === "" || !this.text || !!parseFormula(this.text);
    } catch (e) {
      return false;
    }
  }

  get formulaEvaluation(): string  {
    return this.evaluate(this.text);
  }

  private evaluate(text): string {
    const textForEvaluation = text ? text.trim() : null;
    if (textForEvaluation) {
      let result: string;
      try {
        const value = this.evaluator.evaluate(textForEvaluation);
        result = valueToString(value);
      } catch (e) {
        result = e.errorValue ?? e.message;
      }
      this.result = result;
      return result;
    }
  }

  validateFormulaSyntax(): string {
    if (!this.text) {
      return 'Enter Formula to Evaluate.';
    }

    const failText = "Formula's syntax is not correct. Please fix the structure of your formula";
    const passText = "Formula's Syntax is correct"

    try {
      return parseFormula(this.text) ? passText : failText;
    } catch (e) {
      return failText;
    }
  }

  async update(val: string): Promise<Formula> {
    // this needs to be !== null because setting an empty string is allowed
    // is this ever even called with a null val?
    if (val !== null) {
      this.text = val;
      this.evaluate(val);
    }
    this.updateParent();
    return new Promise(resolve => {
      resolve(this);
    })
  }

  remove(): void {
    this.text = '';
    this.originalText = '';
    Vue.set(this.parent, `${this.fieldName}_formula`, '');
  }

  lookupDynamicFieldValueBy(f: VariableLookupFunction): void {
    this.evaluator = new Evaluator(new FormulaContext(f));
  }

  static get formulaErrors(): object {
    return {
      "#ERROR!": "Unknown error.",
      "#DIV/0!": "You can't divide by zero.",
      "#NAME?": "You wrote a function name that doesn't exist. Check spelling.",
      "#N/A": "You used a formula that isn't returning a value.",
      "#NUM!": "You gave a function a type of number or value that it can't use.",
      "#VALUE!": "You have given text or a space when the function needs a number.",
      "#REF!": "You referred to a form field that doesn't exist.",
      "#NULL!": "You have left something blank or empty that needs a value.",
      "#GETTING_DATA": "Problem getting data.",
    };
  }
}